import { Alert, AlertTitle, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useForm } from "react-hook-form";
import { apiConstants, deleteGeneric, getGeneric, postGeneric } from "../../common/constants"
import { FormProvider, RHFTextField } from "../../common/hook-form";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { useState, useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import "../Wallet/theme.css";
import MUIModal from "../../components/MuiModal";
import { useSnackbar } from "notistack";
import PageTitle from "../../components/PageTitle";
import Loader from "../../common/Loader";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { format } from "date-fns";

function BeneficiaryDetails() {
    const [data, setData] = useState([])
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar();
    const [addBeneficiaryOpen, setAddBeneficiaryOpen] = useState(false)
    const [selectedMerchant, setSelectedMerchant] = useState(null)
    const [deleteBeneficiaryOpen, setDeleteBeneficiaryOpen] = useState(false)
    const [deleteRowData, setDeleteRowData] = useState(null)
    const [makePrimaryOpen, setMakePrimaryOpen] = useState(false)
    const [makePrimaryRowData, setMakePrimaryRowData] = useState(null)
    const [registerIdfcOpen, setRegisterIdfcOpen] = useState(false)
    const [registerIdfcRowData, setRegisterIdfcRowData] = useState(null)

    const methods = useForm({
        defaultValues: {
            merchant: '',
        }
    });

    const { handleSubmit, reset } = methods;

    // Helper function to format account number (show last 4 digits only)
    const formatAccountNumber = (accountNumber) => {
        if (!accountNumber || accountNumber.length < 4) return accountNumber;
        const last4 = accountNumber.slice(-4);
        const masked = '*'.repeat(accountNumber.length - 4);
        return masked + last4;
    };

    // Helper function to format IFSC code (keep first 4 and last 3 chars)
    const formatIfscCode = (ifscCode) => {
        if (!ifscCode || ifscCode.length < 7) return ifscCode;
        const first4 = ifscCode.slice(0, 4);
        const last3 = ifscCode.slice(-3);
        const middle = '*'.repeat(ifscCode.length - 7);
        return first4 + middle + last3;
    };

    // Helper function to format date as "2025-10-17 14:05"
    const formatBeneficiaryDate = (dateString) => {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            return format(date, 'yyyy-MM-dd HH:mm');
        } catch (e) {
            return "-";
        }
    };

    // A beneficiary counts as registered with IDFC only when the bank returned an
    // id for it. Its absence is what blocks an IDFC payout, so it is the one
    // thing worth showing in the grid.
    const isRegisteredWithIdfc = (row) => {
        return Boolean(row?.idfcBeneficiaryId && String(row.idfcBeneficiaryId).trim());
    };

    // Added but sent to no bank, waiting on a named approver. Distinct from
    // "not registered": there is nothing for finance to do here, and the
    // register action would be refused by the server.
    const isPendingApproval = (row) => row?.approvalStatus === 'PENDING_APPROVAL';

    // Helper function to check if bank status is active (case-insensitive)
    const isBankStatusActive = (bankStatus) => {
        return bankStatus?.toLowerCase() === 'active';
    };

    const columns = [
        {
            field: 'beneficiaryName',
            headerName: 'Name',
            flex: 1.2,
            renderCell: ({ row }) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {row.beneficiaryName}
                    </Grid>
                </Grid>
            ),
        },
        {
            field: 'beneficiaryAccountNumber',
            headerName: 'Account Number',
            flex: 1.2,
            renderCell: ({ row }) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {formatAccountNumber(row.beneficiaryAccountNumber)}
                    </Grid>
                </Grid>
            ),
        },
        {
            field: 'beneficiaryBankIfscCode',
            headerName: 'IFSC Code',
            flex: 1.1,
            renderCell: ({ row }) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {formatIfscCode(row.beneficiaryBankIfscCode)}
                    </Grid>
                </Grid>
            ),
        },
        {
            field: 'bankStatus',
            headerName: 'Bank Status',
            flex: 1,
            renderCell: ({ row }) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Chip 
                            label={row.bankStatus || '-'} 
                            color={isBankStatusActive(row.bankStatus) ? 'success' : 'default'}
                            size="small"
                        />
                    </Grid>
                </Grid>
            ),
        },
        {
            field: 'createdAt',
            headerName: 'Added on',
            flex: 1.2,
            renderCell: ({ row }) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {formatBeneficiaryDate(row.createdAt)}
                    </Grid>
                </Grid>
            ),
        },
        {
            field: 'createdBy',
            headerName: 'Added by',
            flex: 1.2,
            renderCell: ({ row }) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {row.createdBy || '-'}
                    </Grid>
                </Grid>
            ),
        },
        {
            field: 'idfcBeneficiaryId',
            headerName: 'IDFC',
            flex: 1.1,
            minWidth: 130,
            renderCell: ({ row }) => {
                const registered = isRegisteredWithIdfc(row);
                const pending = isPendingApproval(row);
                const label = registered ? 'Registered' : pending ? 'Awaiting approval' : 'Not registered';
                const tip = registered
                    ? `IDFC beneficiary id: ${row.idfcBeneficiaryId}`
                    : pending
                        ? `Added by ${row.approvalRequestedBy || 'finance'} and awaiting approval. Nothing has been sent to the bank.`
                        : 'Not registered with IDFC. Payouts to this account will be rejected while IDFC is the active bank.';
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Tooltip title={tip}>
                                <Chip
                                    label={label}
                                    color={registered ? 'success' : pending ? 'info' : 'warning'}
                                    size="small"
                                    variant={registered ? 'filled' : 'outlined'}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                );
            },
        },
        {
            field: 'isPrimary',
            headerName: 'Primary',
            flex: 0.7,
            minWidth: 90,
            renderCell: ({ row }) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Chip 
                            label={row.isPrimary ? 'Yes' : 'No'} 
                            color={row.isPrimary ? 'primary' : 'default'}
                            size="small"
                            sx={{ 
                                fontWeight: row.isPrimary ? 'bold' : 'normal',
                                backgroundColor: row.isPrimary ? '#1976d2' : undefined,
                                color: row.isPrimary ? 'white' : undefined
                            }}
                        />
                    </Grid>
                </Grid>
            ),
        },
        {
            field: 'Action',
            headerName: 'Action',
            flex: 0.5,
            minWidth: 80,
            sortable: false,
            renderCell: ({ row }) => (
                // One menu rather than a row of icons: three actions did not fit,
                // and a menu also lets each one carry a readable label - worth it
                // for an action that moves a merchant onto a different bank.
                <RowActionsMenu
                    row={row}
                    registeredWithIdfc={isRegisteredWithIdfc(row)}
                    pendingApproval={isPendingApproval(row)}
                    bankStatusIsActive={isBankStatusActive(row.bankStatus)}
                    onRegisterIdfc={() => handleRegisterIdfc(row, setRegisterIdfcOpen, setRegisterIdfcRowData)}
                    onMakePrimary={() => handleMakePrimaryBeneficiary(row, setMakePrimaryOpen, setMakePrimaryRowData)}
                    onDelete={() => handleDeleteBeneficiary(row, setDeleteBeneficiaryOpen, setDeleteRowData)}
                />
            )
        }
    ]

    const onSubmit = (data) => {
        getGeneric(`${apiConstants.MERCHANT_DETAILS}/${data.merchant}`).then((response) => {
            setIsLoading(false)
            // Sort by isPrimary (primary beneficiaries first)
            const beneficiaryData = response.data?.beneficiaryConfig || [];
            const sortedData = [...beneficiaryData].sort((a, b) => {
                // Primary beneficiaries first
                if (a.isPrimary && !b.isPrimary) return -1;
                if (!a.isPrimary && b.isPrimary) return 1;
                return 0;
            });
            setData(sortedData)
        }).catch(err => {
            setIsLoading(false)
            enqueueSnackbar(err.response.data.errorMsg, { variant: 'error' })
            console.error(err)
        })
    }

    // Function to get row class name based on isPrimary and bankStatus
    const getRowClassName = (params) => {
        let className = '';
        if (params.row.isPrimary) {
            className += 'primary-beneficiary ';
        }
        if (!isBankStatusActive(params.row.bankStatus)) {
            className += 'inactive-beneficiary ';
        }
        return className.trim();
    };

    function makePrimaryBeneficiary(rowData, resetUpdatingState) {
        if (!rowData) {
            return
        }
        setIsLoading(true)
        // postGeneric(`${apiConstants.MERCHANT_DETAILS}/${rowData.merchant}/makeBeneficiaryConfigPrimary?hashCode=${rowData.hashData}`)
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${rowData.merchant}/makeBeneficiaryConfigPrimary?hashCode=${encodeURIComponent(rowData.hashData)}`)
            .then((response) => {
                enqueueSnackbar('Primary beneficiary updated successfully', { variant: 'success' })
                setIsLoading(false)
                setMakePrimaryOpen(false)
                setMakePrimaryRowData(null)
                onSubmit({ merchant: rowData.merchant })
            })
            .catch(err => {
                setIsLoading(false)
                if (resetUpdatingState) {
                    resetUpdatingState()
                }
                const errorMessage = err?.response?.data?.errorMsg || err?.response?.data?.message || err.message || 'Error updating primary beneficiary'
                enqueueSnackbar(errorMessage, { variant: 'error' })
                console.error(err)
            })
    }



    // Handle Add Beneficiary
    function handleAddBeneficiary(setAddBeneficiaryOpen, setSelectedMerchant) {
        const merchantValue = methods.getValues('merchant')
        setSelectedMerchant(merchantValue)
        setAddBeneficiaryOpen(true)
    }

    // Handle Delete Beneficiary - Opens confirmation dialog
    function handleDeleteBeneficiary(row, setDeleteBeneficiaryOpen, setDeleteRowData) {
        const merchantValue = methods.getValues('merchant')
        if (!merchantValue) {
            enqueueSnackbar('Please select a merchant first', { variant: 'error' })
            return
        }

        setDeleteRowData({ ...row, merchant: merchantValue })
        setDeleteBeneficiaryOpen(true)
    }

    // Handle Send for IDFC approval - Opens confirmation dialog
    function handleRegisterIdfc(row, setRegisterIdfcOpen, setRegisterIdfcRowData) {
        const merchantValue = methods.getValues('merchant')
        if (!merchantValue) {
            enqueueSnackbar('Please select a merchant first', { variant: 'error' })
            return
        }

        setRegisterIdfcRowData({ ...row, merchant: merchantValue })
        setRegisterIdfcOpen(true)
    }

    // Sends no beneficiary details: the backend reads them from merchant_config
    // and reuses the stored beneficiaryCode, so the account keeps one identity
    // across both banks.
    function registerBeneficiaryWithIdfc(rowData, resetRegisteringState) {
        if (!rowData) {
            return
        }
        setIsLoading(true)
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${rowData.merchant}/beneficiary/registerWithBank?hash=${encodeURIComponent(rowData.hashData)}`)
            .then((response) => {
                enqueueSnackbar('Sent for approval. It will be registered with IDFC once approved.', { variant: 'success' })
                setIsLoading(false)
                setRegisterIdfcOpen(false)
                setRegisterIdfcRowData(null)
                onSubmit({ merchant: rowData.merchant })
            })
            .catch(err => {
                setIsLoading(false)
                if (resetRegisteringState) {
                    resetRegisteringState()
                }
                const errorMessage = err?.response?.data?.errorMsg
                    || err?.response?.data?.exception?.message
                    || err?.response?.data?.message
                    || err.message
                    || 'Error sending beneficiary for approval'
                enqueueSnackbar(errorMessage, { variant: 'error' })
                console.error(err)
            })
    }

    // Handle Make Primary Beneficiary - Opens confirmation dialog
    function handleMakePrimaryBeneficiary(row, setMakePrimaryOpen, setMakePrimaryRowData) {
        const merchantValue = methods.getValues('merchant')
        if (!merchantValue) {
            enqueueSnackbar('Please select a merchant first', { variant: 'error' })
            return
        }

        setMakePrimaryRowData({ ...row, merchant: merchantValue })
        setMakePrimaryOpen(true)
    }


    return (
        <>
            {isLoading && <Loader />}
            <PageTitle>Beneficiary Master</PageTitle>

            <AddBeneficiaryModal
                open={addBeneficiaryOpen}
                setOpen={setAddBeneficiaryOpen}
                merchant={selectedMerchant}
                onRefresh={onSubmit}
                setIsLoading={setIsLoading}
                enqueueSnackbar={enqueueSnackbar}
            />
            <DeleteBeneficiaryModal
                open={deleteBeneficiaryOpen}
                setOpen={setDeleteBeneficiaryOpen}
                deleteRowData={deleteRowData}
                onRefresh={onSubmit}
                setIsLoading={setIsLoading}
                enqueueSnackbar={enqueueSnackbar}
            />
            <RegisterIdfcModal
                open={registerIdfcOpen}
                setOpen={setRegisterIdfcOpen}
                registerIdfcRowData={registerIdfcRowData}
                onConfirm={registerBeneficiaryWithIdfc}
            />
            <MakePrimaryBeneficiaryModal
                open={makePrimaryOpen}
                setOpen={setMakePrimaryOpen}
                makePrimaryRowData={makePrimaryRowData}
                onConfirm={makePrimaryBeneficiary}
                enqueueSnackbar={enqueueSnackbar}
            />
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                                <MerchantFilterCached />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LoadingButton
                                    variant="contained"
                                    size="medium"
                                    type="submit"
                                >Submit</LoadingButton>
                                <Button onClick={() => {
                                    reset({
                                        merchant: '',
                                        status: '',
                                        date: '',
                                    })
                                    setData(null)
                                }}
                                    variant="contained" color='error' size="medium" sx={{ ml: 1 }}>Reset</Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </FormProvider>
            {data && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Grid container spacing={2} justifyContent="flex-end">
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddCircleIcon />}
                                    onClick={() => handleAddBeneficiary(setAddBeneficiaryOpen, setSelectedMerchant)}
                                    size="medium"
                                >
                                    Add Beneficiary
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}
            <ProzoDataGrid
                columns={columns}
                autoHeight={true}
                rows={data?.length > 0 ? data.map((item, index) => ({
                    ...item,
                    id: index
                })) : []}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                setPage={setPage}
                page={page}
                pagination={true}
                hideFooterPagination={false}
                filterMode={"server"}
                rowHeight={120}
                getRowHeight={() => 60}
                rowCount={data?.length || 0}
                getRowId={(row) => row.id}
                getRowClassName={getRowClassName}
                sx={{
                    '& .primary-beneficiary': {
                        backgroundColor: '#e3f2fd !important',
                        '&:hover': {
                            backgroundColor: '#bbdefb !important',
                        },
                    },
                    '& .inactive-beneficiary': {
                        backgroundColor: '#f5f5f5 !important',
                        opacity: 0.6,
                        '&:hover': {
                            backgroundColor: '#e0e0e0 !important',
                        },
                    },
                    '& .primary-beneficiary.inactive-beneficiary': {
                        backgroundColor: '#f5f5f5 !important',
                        opacity: 0.6,   
                    },
                }}
            />
        </>
    )
}

export default BeneficiaryDetails



// Add Beneficiary Modal Component
function AddBeneficiaryModal({ open, setOpen, merchant, onRefresh, setIsLoading, enqueueSnackbar }) {
    const [isSubmitting, setIsSubmitting] = useState(false)

    const addBeneficiaryMethods = useForm({
        defaultValues: {
            beneficiaryName: '',
            beneficiaryAccountNumber: '',
            beneficiaryBankIfscCode: ''
        }
    });

    const { handleSubmit: handleAddBeneficiarySubmit, reset: resetAddBeneficiary } = addBeneficiaryMethods;

    const handleClose = () => {
        setOpen(false)
        resetAddBeneficiary()
    };

    const onAddBeneficiarySubmit = (data) => {
        if (!merchant) {
            enqueueSnackbar('Please select a merchant first', { variant: 'error' })
            return
        }

        const payload = {
            beneficiaryName: data.beneficiaryName,
            beneficiaryAccountNumber: data.beneficiaryAccountNumber,
            beneficiaryBankIfscCode: data.beneficiaryBankIfscCode
        }

        setIsSubmitting(true)
        setIsLoading(true)
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchant}/beneficiary`, payload)
            .then((response) => {
                enqueueSnackbar('Beneficiary added successfully', { variant: 'success' })
                setIsSubmitting(false)
                setIsLoading(false)
                handleClose()
                // Refresh the data
                if (merchant && onRefresh) {
                    onRefresh({ merchant })
                }
            })
            .catch(err => {
                setIsSubmitting(false)
                setIsLoading(false)
                const errorMessage = err?.response?.data?.message || err.message || 'Error adding beneficiary'
                enqueueSnackbar(errorMessage, { variant: 'error' })
                console.error(err)
            })
    }

    return (
        <MUIModal open={open} handleClose={handleClose} maxWidth="lg" children={
            <FormProvider methods={addBeneficiaryMethods} onSubmit={handleAddBeneficiarySubmit(onAddBeneficiarySubmit)}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>Important Notice</AlertTitle>
                            A new beneficiary account requires approval before it is sent to the bank. Once approved it becomes the primary beneficiary and all transfers are directed to it.
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <RHFTextField
                            name="beneficiaryName"
                            label="Beneficiary Name"
                            required
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RHFTextField
                            name="beneficiaryAccountNumber"
                            label="Beneficiary Account Number"
                            required
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RHFTextField
                            name="beneficiaryBankIfscCode"
                            label="Beneficiary Bank IFSC Code"
                            required
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={isSubmitting}
                        >
                            Add Beneficiary
                        </LoadingButton>
                    </Grid>
                </Grid>
            </FormProvider>
        }
            title={`Add Beneficiary`} />
    )
}



// Delete Beneficiary Modal Component with Confirmation Dialog
function DeleteBeneficiaryModal({ open, setOpen, deleteRowData, onRefresh, setIsLoading, enqueueSnackbar }) {
    const [isDeleting, setIsDeleting] = useState(false)


    const handleClose = () => {
        setOpen(false)
    }

    const handleConfirmDelete = () => {
        if (!deleteRowData) {
            handleClose()
            return
        }
        setIsDeleting(true)
        setIsLoading(true)
        deleteGeneric(`${apiConstants.MERCHANT_DETAILS}/${deleteRowData?.merchant}/beneficiary?hash=${deleteRowData?.hashData}`)
            .then((response) => {
                enqueueSnackbar('Beneficiary deleted successfully', { variant: 'success' })
                setIsDeleting(false)
                setIsLoading(false)
                handleClose()
                // Refresh the data
                if (deleteRowData?.merchant && onRefresh) {
                    onRefresh({ merchant: deleteRowData.merchant })
                }
            })
            .catch(err => {
                setIsDeleting(false)
                setIsLoading(false)
                const errorMessage = err?.response?.data?.message || err.message || 'Error deleting beneficiary'
                enqueueSnackbar(errorMessage, { variant: 'error' })
                console.error(err)
            })
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                Confirm Delete
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    Are you sure you want to delete this beneficiary? This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isDeleting}
                >
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleConfirmDelete}
                    variant="contained"
                    color="error"
                    loading={isDeleting}
                >
                    Delete
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

// Make Primary Beneficiary Modal Component with Confirmation Dialog
function MakePrimaryBeneficiaryModal({ open, setOpen, makePrimaryRowData, onConfirm, enqueueSnackbar }) {
    const [isUpdating, setIsUpdating] = useState(false)

    // Helper function to format account number (show last 4 digits only)
    const formatAccountNumber = (accountNumber) => {
        if (!accountNumber || accountNumber.length < 4) return accountNumber;
        const last4 = accountNumber.slice(-4);
        const masked = '*'.repeat(accountNumber.length - 4);
        return masked + last4;
    };

    // Helper function to format IFSC code (keep first 4 and last 3 chars)
    const formatIfscCode = (ifscCode) => {
        if (!ifscCode || ifscCode.length < 7) return ifscCode;
        const first4 = ifscCode.slice(0, 4);
        const last3 = ifscCode.slice(-3);
        const middle = '*'.repeat(ifscCode.length - 7);
        return first4 + middle + last3;
    };

    // Reset isUpdating when dialog closes
    useEffect(() => {
        if (!open) {
            setIsUpdating(false)
        }
    }, [open])

    const handleClose = () => {
        setOpen(false)
        setIsUpdating(false)
    }

    const handleConfirmMakePrimary = () => {
        if (!makePrimaryRowData) {
            handleClose()
            return
        }
        setIsUpdating(true)
        onConfirm(makePrimaryRowData, () => setIsUpdating(false))
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                Confirm Make Primary
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
                    Are you sure you want to make this beneficiary the primary beneficiary? All future transfers will be directed to this account.
                </DialogContentText>
                {makePrimaryRowData && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <DialogContentText sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Name:
                            </DialogContentText>
                            <DialogContentText sx={{ ml: 2, mb: 1 }}>
                                {makePrimaryRowData.beneficiaryName || '-'}
                            </DialogContentText>
                        </Grid>
                        <Grid item xs={12}>
                            <DialogContentText sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Account Number:
                            </DialogContentText>
                            <DialogContentText sx={{ ml: 2, mb: 1 }}>
                                {formatAccountNumber(makePrimaryRowData.beneficiaryAccountNumber) || '-'}
                            </DialogContentText>
                        </Grid>
                        <Grid item xs={12}>
                            <DialogContentText sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                IFSC Code:
                            </DialogContentText>
                            <DialogContentText sx={{ ml: 2, mb: 1 }}>
                                {formatIfscCode(makePrimaryRowData.beneficiaryBankIfscCode) || '-'}
                            </DialogContentText>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isUpdating}
                >
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleConfirmMakePrimary}
                    variant="contained"
                    color="primary"
                    loading={isUpdating}
                >
                    Confirm
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

// Register-with-IDFC Modal
//
// Deliberately has no form. The whole point is that the details are already on
// file: re-typing them is what the normal Add path does, and that path cannot be
// reused here because it rejects an account that already exists. Showing the
// stored values read-only lets finance confirm they are sending the right
// account to the bank before anything happens.
function RegisterIdfcModal({ open, setOpen, registerIdfcRowData, onConfirm }) {
    const [isRegistering, setIsRegistering] = useState(false)

    const formatAccountNumber = (accountNumber) => {
        if (!accountNumber || accountNumber.length < 4) return accountNumber;
        const last4 = accountNumber.slice(-4);
        const masked = '*'.repeat(accountNumber.length - 4);
        return masked + last4;
    };

    useEffect(() => {
        if (!open) {
            setIsRegistering(false)
        }
    }, [open])

    const handleClose = () => {
        setOpen(false)
        setIsRegistering(false)
    }

    const handleConfirmRegister = () => {
        if (!registerIdfcRowData) {
            handleClose()
            return
        }
        setIsRegistering(true)
        onConfirm(registerIdfcRowData, () => setIsRegistering(false))
    }

    // IDFC requires an internal transfer when the beneficiary banks with IDFC,
    // and that is decided from the IFSC - so it is worth surfacing here.
    const ifsc = registerIdfcRowData?.beneficiaryBankIfscCode || ''
    const isIdfcAccount = ifsc.trim().toUpperCase().startsWith('IDFB')

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="register-idfc-dialog-title"
            aria-describedby="register-idfc-dialog-description"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="register-idfc-dialog-title">
                Send Beneficiary for IDFC Approval
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="register-idfc-dialog-description" sx={{ mb: 2 }}>
                    These details are already on file and will be sent to IDFC as they are,
                    once the approver authorises it. Nothing needs to be re-entered, and the
                    existing beneficiary record is not replaced.
                </DialogContentText>
                {registerIdfcRowData && (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid item xs={5}><strong>Name</strong></Grid>
                                <Grid item xs={7}>{registerIdfcRowData.beneficiaryName || '-'}</Grid>
                                <Grid item xs={5}><strong>Account Number</strong></Grid>
                                <Grid item xs={7}>{formatAccountNumber(registerIdfcRowData.beneficiaryAccountNumber) || '-'}</Grid>
                                <Grid item xs={5}><strong>IFSC Code</strong></Grid>
                                <Grid item xs={7}>{registerIdfcRowData.beneficiaryBankIfscCode || '-'}</Grid>
                                <Grid item xs={5}><strong>Transfer Mode</strong></Grid>
                                <Grid item xs={7}>
                                    <Chip
                                        label={isIdfcAccount ? 'IFT (IDFC to IDFC)' : 'NEFT / RTGS'}
                                        color={isIdfcAccount ? 'primary' : 'default'}
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}
                <Alert severity="info">
                    <AlertTitle>Before you continue</AlertTitle>
                    This does not send anything to the bank. The account is placed in the
                    approval queue and the approver registers it with IDFC. This action is
                    recorded against your user, and is only available while IDFC is the active
                    payout bank.
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isRegistering}
                >
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleConfirmRegister}
                    variant="contained"
                    color="primary"
                    loading={isRegistering}
                >
                    Send for approval
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

// Per-row actions menu.
//
// Each row owns its anchor element, so the menu is a component rather than
// state on the page - otherwise every row would open the same menu.
//
// Disabled items are kept visible rather than hidden: "Send for IDFC approval"
// greyed out with a reason tells finance the account is already migrated,
// whereas an absent item just looks like the feature is missing.
function RowActionsMenu({ row, registeredWithIdfc, pendingApproval, bankStatusIsActive, onRegisterIdfc, onMakePrimary, onDelete }) {
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const handleOpen = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => setAnchorEl(null)

    // Close first, so the menu is gone behind the confirmation dialog.
    const run = (action) => () => {
        handleClose()
        action()
    }

    // An unapproved account must not become primary: it has no bank id, so
    // every payout for the merchant would then be rejected.
    const canMakePrimary = bankStatusIsActive && !row?.isPrimary
    const makePrimaryReason = row?.isPrimary
        ? 'Already the primary beneficiary'
        : 'Bank status must be Active to make primary'

    return (
        <>
            <Tooltip title="Actions">
                <IconButton size="small" onClick={handleOpen} aria-label="beneficiary actions">
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem
                    onClick={run(onRegisterIdfc)}
                    disabled={registeredWithIdfc || pendingApproval}
                >
                    <ListItemIcon>
                        <AccountBalanceIcon fontSize="small"
                            color={(registeredWithIdfc || pendingApproval) ? 'disabled' : 'success'} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Send for IDFC approval"
                        secondary={registeredWithIdfc
                            ? `Already registered as ${row.idfcBeneficiaryId}`
                            : pendingApproval
                                ? 'Waiting for approval; it will be registered once approved'
                                : 'Uses the details already on file. The approver registers it with the bank.'}
                    />
                </MenuItem>
                <MenuItem
                    onClick={run(onMakePrimary)}
                    disabled={!canMakePrimary || pendingApproval}
                >
                    <ListItemIcon>
                        <SettingsIcon fontSize="small" color={canMakePrimary ? 'primary' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Make Primary"
                        secondary={canMakePrimary ? 'All transfers will go to this account' : makePrimaryReason}
                    />
                </MenuItem>
                <Divider />
                <MenuItem onClick={run(onDelete)}>
                    <ListItemIcon>
                        <DeleteForeverIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Delete Beneficiary" />
                </MenuItem>
            </Menu>
        </>
    )
}
